import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useEffect, useState } from 'react';
import WebService from '../../Services/WebService';
import { setMenus } from '../../redux/slices/menuSlice';
import { ICON_MAP } from "../../utils/iconMapper";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { setTierUpgradePendingCount, setTruckStopPendingCount, setRequestCallbackCount, setDriverPendingCount, setPendingRewardsFromTruckStopOwner, setReviewFlagCount, setReportedIssueCount } from '../../redux/slices/trainingSlice';

const VerticalMenu = () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const dispatch = useDispatch();
    const menus = useSelector((state: RootState) => state.menus.items);
    const truckStopPendingCount = useSelector((state: RootState) => state.training.truck_stop_pending_count);
    const tierUpgradePendingCount = useSelector((state: RootState) => state.training.tier_upgrade_pending_count);
    const driverPendingCount = useSelector((state: RootState) => state.training.driver_pending_count);
    const pendingRewardsFromTruckStopOwner = useSelector((state: RootState) => state.training.pending_rewards_from_truck_stop_owner);
    const reviewFlagPendingCount = useSelector((state: RootState) => state.training.review_flag_pending_count);
    const requestCallbackCount = useSelector((state: RootState) => state.training.requestcallback_count);
    const reportedIssueCount = useSelector((state: RootState) => state.training.reported_issue_pending_count);
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [callbackCount, setCallbackCount] = useState<number>(0);
    const [truckStopCount, setTruckStopCount] = useState<number>(0);
    const [driverCount, setDriverCount] = useState<number>(0);
    const [tierCount, setTierCount] = useState<number>(0);
    const [rewardCount, setRewardCount] = useState<number>(0);
    const [reviewCount, setReviewCount] = useState<number>(0);
    const [issueCount, setIssueCount] = useState<number>(0);

    useEffect(() => {
        const getStoredCount = (key: string) => Number(localStorage.getItem(key) || 0);

        const fetchMenus = () => {
            WebService.getAPI({
                action: "profile/my-menus",
                isShowError: true
            }).then((res: any) => {
                if (res?.status) {
                    dispatch(setMenus(res?.data));
                }
            });
        };

        // reusable function
        const updateCounts = (data: any) => {
            const count = data?.hasOwnProperty("requestcallback_count")
                ? Number(data.requestcallback_count || 0)
                : getStoredCount("requestcallback_count");
            const truckStop = data?.hasOwnProperty("truck_stop_pending_count")
                ? Number(data.truck_stop_pending_count || 0)
                : getStoredCount("truckstop_count");
            const driver = data?.hasOwnProperty("driver_pending_count")
                ? Number(data.driver_pending_count || 0)
                : getStoredCount("driver_count");
            const tier = data?.hasOwnProperty("tier_upgrade_pending_count")
                ? Number(data.tier_upgrade_pending_count || 0)
                : getStoredCount("tier_count");
            const reward = data?.hasOwnProperty("pending_rewards_from_truck_stop_owner")
                ? Number(data.pending_rewards_from_truck_stop_owner || 0)
                : getStoredCount("reward_count");
            const review = data?.hasOwnProperty("review_flag_pending_count")
                ? Number(data.review_flag_pending_count || 0)
                : getStoredCount("review_count");
            const issue = data?.hasOwnProperty("reported_issue_pending_count")
                ? Number(data.reported_issue_pending_count || 0)
                : getStoredCount("issue_count");

            // redux
            dispatch(setRequestCallbackCount(count));
            dispatch(setTruckStopPendingCount(truckStop));
            dispatch(setDriverPendingCount(driver));
            dispatch(setTierUpgradePendingCount(tier));
            dispatch(setPendingRewardsFromTruckStopOwner(reward));
            dispatch(setReviewFlagCount(review));
            dispatch(setReportedIssueCount(issue));

            // local state
            setCallbackCount(count);
            setTruckStopCount(truckStop);
            setDriverCount(driver);
            setTierCount(tier);
            setRewardCount(reward);
            setReviewCount(review);
            setIssueCount(issue);

            // local storage
            localStorage.setItem("requestcallback_count", String(count));
            localStorage.setItem("truckstop_count", String(truckStop));
            localStorage.setItem("driver_count", String(driver));
            localStorage.setItem("tier_count", String(tier));
            localStorage.setItem("reward_count", String(reward));
            localStorage.setItem("review_count", String(review));
            localStorage.setItem("issue_count", String(issue));
        };

        // Initial API call
        fetchMenus();

        // Load cached counts initially
        setCallbackCount(getStoredCount("requestcallback_count"));
        setTruckStopCount(getStoredCount("truckstop_count"));
        setDriverCount(getStoredCount("driver_count"));
        setTierCount(getStoredCount("tier_count"));
        setRewardCount(getStoredCount("reward_count"));
        setReviewCount(getStoredCount("review_count"));
        setIssueCount(getStoredCount("issue_count"));

        // =========================
        // SSE CONNECTION
        // =========================

        const token = localStorage.getItem("access_token");
        if (!token) return;

        const streamBaseUrl = BASE_URL
            ? `${BASE_URL.replace(/\/?$/, "/")}profile/my-menus/stream`
            : "/profile/my-menus/stream";

        const es = new EventSource(
            `${streamBaseUrl}?token=${encodeURIComponent(token)}`
        );

        const handleStreamMessage = (e: MessageEvent) => {
            try {
                updateCounts(JSON.parse(e.data));
            } catch (err:any) {
                console.error("Invalid sidebar counts SSE payload:", err?.response?.data?.message);
            }
        };

        // first snapshot
        es.addEventListener("snapshot", handleStreamMessage);

        // realtime updates
        es.addEventListener("update", handleStreamMessage);

        es.onerror = (err) => {
            console.error("SSE Error:", err);
        };

        // cleanup
        return () => {
            es.close();
        };

    }, [BASE_URL, dispatch]);

    const callbackBadgeCount = requestCallbackCount ?? callbackCount;
    const truckStopBadgeCount = truckStopPendingCount ?? truckStopCount;
    const driverBadgeCount = driverPendingCount ?? driverCount;
    const tierBadgeCount = tierUpgradePendingCount ?? tierCount;
    const rewardBadgeCount = pendingRewardsFromTruckStopOwner ?? rewardCount;
    const reviewBadgeCount = reviewFlagPendingCount ?? reviewCount;
    const issueBadgeCount = reportedIssueCount ?? issueCount;

    const toggleMenu = (menuId: string) => {
        setOpenMenu(prev => (prev === menuId ? null : menuId));
    };

    return (
        <>
            <div id="vertical_menu" className="verticle-menu">
                <SimpleBar style={{ maxHeight: 'calc(100vh - 100px)', overflowX: 'hidden' }}>
                    <div className="menu-list">
                        {menus.filter((menu: any) => menu.can_read === 1)
                            .map((menu: any) => {
                                const Icon = ICON_MAP[menu.icon] || ICON_MAP["default"];
                                const isParentActive =
                                    menu.subMenus?.some(
                                        (sub: any) => location.pathname === sub.url
                                    );

                                // ✅ MENU WITHOUT SUBMENUS
                                if (!menu.subMenus || menu.subMenus.length === 0) {
                                    return (
                                        <Link
                                            key={menu.menu_id}
                                            to={menu.url}
                                            className={`menu-item ${location.pathname === menu.url ? "active" : ""
                                                }`}
                                        >
                                            <Icon size={20} className="menu-icon" />
                                            <span>{menu.title}</span>
                                            {menu?.url === "/request-callback" && callbackBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {callbackBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/truck-stop-owner" && truckStopBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {truckStopBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/driver" && driverBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {driverBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/tier-upgrade-request" && tierBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {tierBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/reward" && rewardBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {rewardBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/review-feedback" && reviewBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {reviewBadgeCount}
                                                </span>
                                            )}
                                            {menu?.url === "/reported-alerts" && issueBadgeCount > 0 && (
                                                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                    {issueBadgeCount}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                }

                                // ✅ MENU WITH SUBMENUS
                                return (
                                    <div key={menu.menu_id} className="menu-group">
                                        <div style={{ cursor: 'pointer' }}
                                            className={`menu-item parent ${isParentActive ? "active" : ""
                                                }`}
                                            onClick={() => toggleMenu(menu.menu_id)}
                                        >
                                            <Icon size={20} className="menu-icon" />
                                            <span>{menu.title}</span>
                                            <span className="arrow">
                                                {openMenu === menu.menu_id ? "▾" : "▸"}
                                            </span>
                                        </div>

                                        {openMenu === menu.menu_id && (
                                            <div className="submenu-list ss">
                                                {menu.subMenus
                                                    .filter((sub: any) => sub.can_read === 1)
                                                    .map((sub: any) => {
                                                        const SubIcon = ICON_MAP[sub.icon] || MdOutlineKeyboardArrowRight;

                                                        return (
                                                            <Link
                                                                key={sub.menu_id}
                                                                to={sub.url}
                                                                className={`submenu-item ${location.pathname === sub.url ? "active" : ""
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {/* arrow */}
                                                                    <MdOutlineKeyboardArrowRight size={14} className="text-gray-400" />

                                                                    {/* submenu icon */}
                                                                    <SubIcon size={16} className="submenu-icon" />

                                                                    {/* title */}
                                                                    <span>{sub.title}</span>

                                                                    {sub?.url === "/request-callback" && callbackBadgeCount > 0 && (
                                                                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                                            {callbackBadgeCount}
                                                                        </span>
                                                                    )}
                                                                    {sub?.url === "/truck-stop-owner" && truckStopBadgeCount > 0 && (
                                                                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                                            {truckStopBadgeCount}
                                                                        </span>
                                                                    )}
                                                                    {sub?.url === "/driver" && driverBadgeCount > 0 && (
                                                                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                                            {driverBadgeCount}
                                                                        </span>
                                                                    )}
                                                                    {sub?.url === "/tier-upgrade-request" && tierBadgeCount > 0 && (
                                                                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                                            {tierBadgeCount}
                                                                        </span>
                                                                    )}
                                                                    {sub?.url === "/reward" && rewardBadgeCount > 0 && (
                                                                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-[2px] rounded-full">
                                                                            {rewardBadgeCount}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </Link>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </SimpleBar>
            </div>
        </>
    )
}
export default VerticalMenu;
